/* eslint-disable @typescript-eslint/no-use-before-define */
import {AttrsSchema, Component} from 'panel';
import round from 'lodash/round';
import type {Point} from 'highcharts';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';
import {capitalize} from 'common/util/string';
import {getDownloadMode, getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {BarType, FunnelStepSegmentSelection, SelectedBar} from 'common/report/funnels/charts/funnels-step-chart/types';
import {commaizeNumber} from 'common/util';
import {TrackedEvents} from 'common/report/funnels/util/tracking';
import {isNumericSegment} from 'common/query-builder/query-block/segment-menu/types';
import {getDataGroupName} from 'common/report/funnels/util/index';
import {ChartVizType, DownloadMode, ReportName} from 'common/report/constants';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {
  ChartType,
  CohortType,
  ColorChoice,
  DateKeyedFunnelSegments,
  FunnelBookmark,
  FunnelSegment,
  StepSelection,
} from 'common/report/funnels/models/types';
import {OVERALL_PROP} from 'common/report/funnels/models/constants';
import {createBucketLineChartFunnelData, getRenamedOrderedSegmentSteps} from 'common/report/funnels/models/utils';
import {
  convertSegmentLabelForCohort,
  createFunnelDataWithRename,
  getBucketSize,
  getCountTypeText,
  getOverallStepsFromDatedSegments,
  getSegments,
  getSelectedSegments,
  hasData,
} from 'common/report/funnels/util';
import {stepSelectorAttributes} from 'common/report/funnels/visualization/util';
import {FrequencyViewType} from 'common/report/funnels/models/frequency-chart/types';
import {FunnelQueryResponse, SankeyResponse} from 'common/report/funnels/models/api-types';
import {formatTime} from 'common/util/date';
import {currentProjectStore} from 'common/report/stores/project-details';
import {useStore} from 'common/util/component-helpers';
import {
  getBarChartData,
  getBucketData,
  getFunnelStep,
  percentOfTotal,
} from 'common/report/funnels/models/bar-chart/utils';
import type {SelectedBar as BarChartSelectedBar} from 'common/report/funnels/models/bar-chart/types';
import {getBucketAccessor, getMedianAccessor, labelFormatter} from 'common/report/funnels/models/time-to-convert/utils';
import {conversionMode, FunnelStepBucketAccessors} from 'common/report/funnels/models/frequency-chart/utils';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {deselectPoint, selectPoint} from 'common/report/util/highcharts';
import {openUserSidebar} from 'common/widgets/user-sidebar';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {CohortDetail} from 'common/widgets/create-cohort-button';
import {CalendarDateRange, DateRangeType} from 'common/widgets/date-range-picker/models/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {PopoverTracking, SlottedContent} from 'common/report/funnels/visualization/chart-display/utils/popover';
import * as cohorts from 'common/report/funnels/visualization/chart-display//utils/cohorts';
import {
  ConversionTimeDescriptor,
  FrequencyBucketDescriptor,
} from 'common/report/funnels/visualization/chart-display/utils/cohorts';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {ReportSwitchEvent} from 'common/query-builder/query-builder/types';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {SectionItem} from 'common/components/items-menu/types';
import {StepChartBookmarkController} from 'common/report/funnels/charts/funnels-step-chart/bookmark-controller';
import {FunnelsStepChartBookmarkController} from 'common/report/funnels/charts/funnels-step-chart/bookmark-controller/funnels';

import css from './index.inline.styl';
import {template} from './template';

const TTC_DRILLDOWN = `drilldown time to convert`;

// TODO rename this as the line charts also use it, maybe e.g. TtcFreqPopoverSelection
export interface BarChartPopover {
  // Required to uniquely identify the segment
  segment: {
    count: number;
    label: string;
    name: string | symbol;
    color: string;
  };
  // The label for the bar
  // Ex: for ttc, the label for the time to convert period (e.g. "0 - 2 days")
  // Ex: for frequency, the label for the frequency, i.e. number of times
  rowLabel: string;
  // The index for the bar
  // Ex: for ttc, the index of this time period within the ttc period (e.g. if the interval is [0, 10] with a step of [1 day],
  // the 0 index would be "0 - 1 day" and the 3 index would be "3 - 4 days"
  // Ex: for frequency, the index of this frequency segment (i.e. 1 time is index 0, 4 times is index 3)
  queryIndex?: number;
  cohortDetails?: CohortDetail[];
  cohort?: Cohort;

  clientX?: number;
  clientY?: number;

  /** the selected point, if this is a line chart */
  point?: Point;
}

export enum ViewUsersType {
  Converted = `view-users-converted`,
  ViewUsers = `view-users`,
  DroppedOff = `view-users-dropped-off`,
}

export enum StepPopoverAction {
  ViewInFlows = `view-in-flows`,
  ViewUsers = `view-users`,
}

export interface StepPopoverActionParams {
  action: StepPopoverAction;
  stepIndex: number;
  segmentProp: string;
  isDropOff: boolean;
  color: string;
  metricLabel: string;
  barType: BarType;
  isComparison: boolean;
  comparisonDateRange: CalendarDateRange;
}

export type PopoverParams = {
  /** whether popover is open on the chart */
  open: boolean;
  /** which bar on the chart the popover is targeting */
  bar?: SelectedBar;
  groupByValue?: string;
  /** descriptors of the cohorts which can be created from this bar */
  cohortDetails?: CohortDetail[];
  cohort?: Cohort;
  /** positioning information for the popover */
  styles?: any;
  isSegmented?: boolean;
  isDropOff?: boolean;
  /** displayable segment for the selected bar */
  segmentProp?: string;
  /** header for the popover */
  title?: string;
  /**
   * available actions within the popover
   *
   * TODO for values because it's an akward mix of types at the moment
   */
  /** text to be displayed in the popover */
  buttonOptions?: Array<{label: string; value?: string; values?: TODO[]}>;
  slottedContent?: SlottedContent[];

  onAction?: (ev: CustomEvent<{action: string; item: any}>) => void; // TODO typing kinda loose here
  onClose?: () => void;
};

interface State {
  popoverState: PopoverParams;

  ttcSelection: BarChartPopover;
  frequencySelection: BarChartPopover;

  selectedFunnelStepSegment: null | FunnelStepSegmentSelection;

  funnelQuery: FunnelsQuery;

  segmentData: DateKeyedFunnelSegments[];
  comparisonSegmentData: DateKeyedFunnelSegments[];
}

interface FunnelsChartDisplayAttrs {
  'resizing-layout': boolean;
  'bookmark-name': string;
  'rca-source-id'?: number;
}

interface FunnelsChartDisplayProps {
  bookmarkParams: FunnelBookmark;
  queryResults: FunnelQueryResponse;
  dataGroups: DataGroup[];
}

export type UpdateEvent = CustomEvent<{delta: Partial<FunnelsQuery>}>;
export type TrackEvent = CustomEvent<{eventName: string; props: TODO}>;
export interface FunnelsChartDisplayJsxProps {
  attrs: FunnelsChartDisplayAttrs;
  props: FunnelsChartDisplayProps;
  on?: {
    update: (ev: UpdateEvent) => void;
    track: (ev: TrackEvent) => void;
  };
}

export class FunnelsChartDisplay extends Component<
  State,
  FunnelsChartDisplayAttrs,
  EmptyObj,
  void,
  IronContextRegistry
> {
  private _bookmarkParams: FunnelBookmark;
  private _queryResults: FunnelQueryResponse;
  private _dataGroups: DataGroup[];
  private _bookmarkController: StepChartBookmarkController;

  static get attrsSchema(): AttrsSchema<FunnelsChartDisplayAttrs> {
    return {
      ...super.attrsSchema,
      'resizing-layout': `boolean`,
      'bookmark-name': `string`,
      'rca-source-id': `number`,
    };
  }

  set bookmarkParams(params: FunnelBookmark) {
    this._bookmarkParams = params;
    this.update({funnelQuery: FunnelsQuery.fromBookmark(params)});
  }

  get bookmarkParams() {
    return this._bookmarkParams;
  }

  set queryResults(results: FunnelQueryResponse) {
    this._queryResults = results;

    if (this.bookmarkParams && results) {
      this._bookmarkController = new FunnelsStepChartBookmarkController(this._bookmarkParams, results);
      const {data} = this.queryResults;
      const compareData = this.queryResults.compare_data;
      const segmentData = createFunnelDataWithRename(data, this.state.funnelQuery);
      const comparisonSegmentData = compareData
        ? createFunnelDataWithRename(compareData, this.state.funnelQuery)
        : null;
      this.update({segmentData, comparisonSegmentData});
    }
  }

  get queryResults() {
    return this._queryResults;
  }

  set dataGroups(groups: DataGroup[]) {
    this._dataGroups = groups;
    this.update();
  }

  get dataGroups() {
    return this._dataGroups;
  }

  get projectId() {
    return getProjectId();
  }

  get workspaceId() {
    return getWorkspaceId();
  }

  get savedReportName() {
    return this.attr(`bookmark-name`);
  }

  /** add 1 if non-null to account for between-step data (like time to convert) being on the second step */
  get shiftedEventIndex() {
    return this.state.funnelQuery.eventIndex !== null ? this.state.funnelQuery.eventIndex + 1 : null;
  }

  getProjectAsOfDate() {
    return currentProjectStore.value?.projectAsOfDate || ``;
  }

  getProjectTimezone() {
    return currentProjectStore.value?.timezone || ``;
  }

  getActiveDataGroup() {
    const activeDataGroupId = this.state.funnelQuery.dataGroupId;

    if (activeDataGroupId && dataGroupStore.value) {
      return dataGroupStore.value[activeDataGroupId];
    }
    return null;
  }

  isDownloadMode() {
    return getDownloadMode() !== DownloadMode.None;
  }

  isQuerySegmented() {
    return this.state.funnelQuery.isSegmented();
  }

  isQueryGroupedSteps() {
    return this.state.funnelQuery.isGroupedSteps();
  }

  get hasComparison() {
    return hasData(this.state.comparisonSegmentData);
  }

  connectedCallback() {
    super.connectedCallback();
    useStore(this, currentProjectStore).fetch();
    useStore(this, dataGroupStore);
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      contexts: [`reportLayoutManager` as const, `urlManager` as const],
      defaultState: {
        popoverState: {
          open: false,
        },
        ttcSelection: null,
        frequencySelection: null,
        selectedFunnelStepSegment: null,
        funnelQuery: null,
        segmentData: [],
        comparisonSegmentData: [],
      },
      helpers: {
        isDownloadMode: () => this.isDownloadMode(),
        trackEventFromChild: (ev) => {
          ev.stopPropagation();
          // data table track events use `props` instead of `properties`
          const {eventName, properties, props} = ev.detail;
          this.track(eventName, properties || props);
        },

        getTopPathsProp: () => {
          return {
            funnelsResponse: (this.queryResults as TODO) as SankeyResponse,
            bookmark: this.state.funnelQuery.generateBookmark(),
          };
        },
        // Base default props used by most charts
        getDefaultProps: () => ({
          funnelsResponse: this.queryResults,
          bookmark: this.state.funnelQuery.generateBookmark(),
          selectedSegment: this.state.selectedFunnelStepSegment,
        }),

        changeControlGroup: (ev) => {
          this.updateQuery({
            controlGroup: ev.detail.props.map((p) => p.prop),
          });
        },

        handleViewUsersForSegment: ({
          stepLabel,
          segment,
          isDropOff,
          cohort,
          cohortDetails,
          color = ``,
          selectedDateRange = null,
        }: {
          stepLabel: string;
          segment: string;
          isDropOff: boolean;
          cohort: Cohort;
          cohortDetails: CohortDetail[];
          color: string;
          selectedDateRange?: {
            from: string;
            to: string;
            type: DateRangeType;
          };
        }) => {
          this.track(PopoverTracking.VIEW_USERS, {
            chart: `funnels-chart`,
          });
          const {groupBy: groupByList} = this.state.funnelQuery;
          openUserSidebar(
            {
              'hide-cohort-functionality': groupByList.some(isNumericSegment),
              'include-all-users': true,
              'report-name': ReportName.Funnels,
              segments: [stepLabel, segment].filter(Boolean),
              'segment-label': isDropOff ? `who dropped off` : `who converted`,
              color,
              'data-group-name': getDataGroupName({plural: false, dataGroup: this.getActiveDataGroup()}),
              ...(selectedDateRange
                ? {
                    'from-date': selectedDateRange.from,
                    'to-date': selectedDateRange.to,
                  }
                : {}),
            },
            {
              cohort,
              cohortDetails,
            },
          );
        },

        handleViewInFlows: ({stepIndex}: {stepIndex: number}) => {
          const detail = {
            report: BookmarkType.Flows,
            newUrlPath: getReportUrl(`flows`, {
              projectId: this.projectId,
              workspaceId: this.workspaceId,
            }),
            additionalContext: {viewAsFlowsContext: {funnelStep: stepIndex - 1}},
          };
          const event: ReportSwitchEvent = new CustomEvent(`reportswitch`, {detail, composed: true, bubbles: true});
          this.track(TrackedEvents.ViewAsFlowsClicked);
          this.dispatchEvent(event);
        },

        popoverCallback: (segmentName: string): SectionItem[] => {
          if (segmentName === OTHER_SEGMENT_VALUE) {
            return [];
          }
          const disableMessage = this._bookmarkController.getDisableViewUsersButtonMessage();
          const hasRCAOption = !!this.attr(`rca-source-id`) && this.getChartVizType() === ChartVizType.Line;
          const options = [
            {
              label: `View Users`,
              icon: `team`,
              value: ViewUsersType.ViewUsers,
              ...(disableMessage
                ? {
                    isDisabled: true,
                    tooltip: {text: disableMessage},
                  }
                : {}),
            },
          ];
          if (hasRCAOption) {
            options.push({
              label: `Root Cause Analysis`,
              icon: `analysis`,
              value: `RCA` as any,
            });
          }
          return options;
        },

        popover: {
          handlePopoverClose: () => {
            this.closePopover();
          },
          handleAction: ({
            action,
            stepIndex,
            segmentProp,
            isDropOff,
            color,
            barType,
            isComparison,
            comparisonDateRange,
          }: StepPopoverActionParams) => {
            const stepLabel = `Step ${stepIndex + 1} ${this.state.funnelQuery.getStepLabels()[stepIndex]}`;
            if (action === StepPopoverAction.ViewUsers) {
              const behaviorDescriptor = {
                index: stepIndex,
                selectedBarType: barType,
                groupByValue: segmentProp,
              };
              const reportName = this.savedReportName;

              const segmentName = segmentProp;
              const cohortDetails = cohorts.getStepsCohortDetails({
                isComparison,
                comparisonDateRange,
                behaviorDescriptor,
                reportName,
                projectAsOfDate: this.getProjectAsOfDate(),
                ...(segmentName ? {segment: convertSegmentLabelForCohort(segmentName, this.getSegmentNames())} : {}),
                query: this.state.funnelQuery,
                dataGroup: this.getActiveDataGroup(),
              });

              this.helpers.handleViewUsersForSegment({
                segment: segmentProp,
                stepLabel,
                isDropOff,
                cohort: cohortDetails[0].cohort,
                cohortDetails,
                color,
              });
            } else if (action === StepPopoverAction.ViewInFlows) {
              this.helpers.handleViewInFlows({stepIndex});
            } else {
              throw new Error(`unsupported action`);
            }
          },
        },

        trend: {
          handlePopoverAction: (ev) => {
            const {action, color, segment, stepIndex, isComparison, comparisonDateRange, selectedDateRange} = ev.detail;

            const reportName = this.savedReportName;
            const behaviorDescriptor = {
              index: stepIndex,
              selectedBarType: BarType.CONVERTED,
              groupByValue: segment,
            };
            const cohortDetails = cohorts.getStepsCohortDetails({
              isComparison,
              comparisonDateRange,
              behaviorDescriptor,
              reportName,
              projectAsOfDate: this.getProjectAsOfDate(),
              ...(segment !== OVERALL_PROP
                ? {segment: convertSegmentLabelForCohort(segment, this.getSegmentNames())}
                : {}),
              query: this.state.funnelQuery,
              dataGroup: this.getActiveDataGroup(),
              selectedDateRange,
            });
            const stepLabel = `Step ${stepIndex + 1} ${this.state.funnelQuery.getStepLabels()[stepIndex]}`;
            // TODO @Jun iron/common/report/insights/visualization/charts/chart-display/index.ts
            // need to handle the same logic in handleViewUsersForSegment
            if (action === ViewUsersType.ViewUsers) {
              this.helpers.handleViewUsersForSegment({
                stepLabel,
                segment: segment !== OVERALL_PROP ? segment : ``,
                isDropOff: false,
                cohort: cohortDetails[0].cohort,
                cohortDetails,
                color,
                selectedDateRange,
              });
            } else if (action === `RCA`) {
              this.handleRCAClicked(selectedDateRange);
            }
          },
        },

        steps: {
          handleMetricChartPopoverAction: (ev) => {
            const {action, color, segment, stepIndex, isComparison, comparisonDateRange} = ev.detail;

            const reportName = this.savedReportName;
            const behaviorDescriptor = {
              index: stepIndex,
              selectedBarType: BarType.CONVERTED,
              groupByValue: segment,
            };

            const cohortDetails = cohorts.getStepsCohortDetails({
              isComparison,
              comparisonDateRange,
              behaviorDescriptor,
              reportName,
              projectAsOfDate: this.getProjectAsOfDate(),
              ...(segment !== OVERALL_PROP
                ? {segment: convertSegmentLabelForCohort(segment, this.getSegmentNames())}
                : {}),
              query: this.state.funnelQuery,
              dataGroup: this.getActiveDataGroup(),
            });
            const stepLabel = `Step ${stepIndex + 1} ${this.state.funnelQuery.getStepLabels()[stepIndex]}`;
            // TODO @Jun iron/common/report/insights/visualization/charts/chart-display/index.ts
            // need to handle the same logic in handleViewUsersForSegment
            if (action === ViewUsersType.ViewUsers) {
              this.helpers.handleViewUsersForSegment({
                stepLabel,
                segment: segment !== OVERALL_PROP ? segment : ``,
                isDropOff: false,
                cohort: cohortDetails[0].cohort,
                cohortDetails,
                color,
              });
            }
          },
        },

        timeToConvert: {
          getAttrs: () => ({
            'include-header': true,
            'hide-tooltip': !!this.state.ttcSelection,
          }),
          getAggregateAttrs: () => ({
            'project-id': getProjectId(),
            'hide-tooltip': !!this.state.ttcSelection,
            'project-as-of-date': this.getProjectAsOfDate(),
            'project-timezone': this.getProjectTimezone(),
          }),

          /** Popover Methods */
          popover: {
            popoverTitle: () => {
              const {ttcSelection} = this.state;
              return ttcSelection ? ttcSelection.segment.label : null;
            },
            popoverBody: () => {
              const query = this.state.funnelQuery;
              const {ttcSelection} = this.state;
              const {countType, dataGroupId} = query;

              if (ttcSelection) {
                const {
                  segment: {count, name},
                } = this.state.ttcSelection;
                const segment: FunnelSegment = this.getRenamedOrderedSegmentSteps().find(
                  (segment) => segment.prop === name,
                );
                const funnelStep = getFunnelStep(segment.steps, this.shiftedEventIndex);
                const bucketData = getBucketData(funnelStep, getBucketAccessor(this.shiftedEventIndex));
                const countTypeText = getCountTypeText({
                  funnelsCountType: countType,
                  dataGroupId,
                });
                return [
                  {
                    count: `${commaizeNumber(count)} ${countTypeText}`,
                    percent: `${percentOfTotal(bucketData, count)}% conversions`,
                    subText: ttcSelection.rowLabel,
                  },
                ];
              }
              return null;
            },
            buttonOptions: () => {
              const options = [];
              const disableMessage = this._bookmarkController.getDisableViewUsersButtonMessage();
              const dataGroup = capitalize(getDataGroupName());
              options.push({
                label: `View ${dataGroup}`,
                icon: `team`,
                value: ViewUsersType.ViewUsers,
                tooltip: `View ${dataGroup} in this step`,
                ...(disableMessage
                  ? {
                      isDisabled: true,
                      tooltip: disableMessage,
                    }
                  : {}),
              });

              if (this.state.ttcSelection && this.state.ttcSelection.segment.count > 1) {
                options.push({label: `Zoom`, value: `zoom`});
              }
              return options;
            },
            closePopover: () => {
              if (this.state.ttcSelection?.point) {
                deselectPoint(this.state.ttcSelection.point);
              }
              this.update({ttcSelection: null});
            },
            openPopover: (ev: CustomEvent) => {
              this.track(PopoverTracking.CLICK_OPEN, {
                chart: ChartType.TimeToConvert,
              });
              const ttcSelection = ev.detail.selection as BarChartPopover;
              if (ttcSelection.point) {
                selectPoint(ttcSelection.point);
              }
              const comparisonDateRange = ev.detail.comparisonDateRange as Nullable<CalendarDateRange>;
              const {steps: stepData} = this.getRenamedOrderedSegmentSteps()[0];
              let stepIndex = null;
              let fromStart = false;
              if (this.shiftedEventIndex) {
                stepIndex = this.shiftedEventIndex;
                fromStart = false;
              } else {
                stepIndex = stepData.length - 1;
                fromStart = true;
              }
              const funnelStep = getFunnelStep(stepData, this.shiftedEventIndex);
              const bucketData = getBucketData(funnelStep, getBucketAccessor(this.shiftedEventIndex));
              const groupByValue = ttcSelection.segment.name;
              const behaviorDescriptor = {
                bucket: ttcSelection.queryIndex,
                bucketSize: bucketData.buckets.length,
                fromStart,
                index: stepIndex,
                groupByValue,
                type: CohortType.TIME_BUCKET,
              } as ConversionTimeDescriptor;

              const conversionRange = ttcSelection.rowLabel;
              const segment = convertSegmentLabelForCohort(ttcSelection.segment.label, this.getSegmentNames());
              const index = Number.isInteger(this.state.funnelQuery.eventIndex)
                ? this.state.funnelQuery.eventIndex
                : this.state.funnelQuery.events.length - 1;
              const query = new FunnelsQuery(this.state.funnelQuery);
              if (comparisonDateRange) {
                query.dateRange = new DateRange({
                  type: DateRangeType.Between,
                  from: comparisonDateRange.from,
                  to: comparisonDateRange.to,
                });
              }
              const cohort = cohorts.createCohort({
                bookmark: query.generateBookmark(),
                query,
                behaviorDescriptor,
                description: this.getCohortDescription({
                  behaviorDescriptor,
                  segment,
                  dataGroup: this.getActiveDataGroup(),
                  conversionRange,
                  selectedSteps: this.stepSelected(),
                }),
                name: cohorts.getCohortTitle({
                  index,
                  type: CohortType.TIME_BUCKET,
                  reportName: this.savedReportName,
                  dataGroup: this.getActiveDataGroup(),
                  conversionRange,
                  selectedSteps: this.stepSelected(),
                  ...(this.isQuerySegmented() && {segment}),
                }),
              });
              const cohortDetails = [
                {
                  type: CohortType.TIME_BUCKET,
                  label: `Create Cohort`,
                  warning: ``,
                  cohort,
                },
              ];
              this.update({
                ttcSelection: {
                  ...ttcSelection,
                  cohortDetails,
                  cohort,
                },
              });
            },
            handlePopoverAction: ({detail: {action}}) => {
              const {ttcSelection, funnelQuery} = this.state;

              if (action === `zoom`) {
                this.drilldown(ttcSelection.queryIndex);
                this.helpers.timeToConvert.popover.closePopover();
              } else if (action === ViewUsersType.ViewUsers) {
                this.track(PopoverTracking.VIEW_USERS, {
                  chart: ChartType.TimeToConvert,
                });
                const {groupBy: groupByList} = funnelQuery;
                openUserSidebar(
                  {
                    'hide-cohort-functionality': groupByList.some(isNumericSegment),
                    'include-all-users': true,
                    'report-name': ReportName.Funnels,
                    segments: [ttcSelection.rowLabel, this.helpers.timeToConvert.popover.popoverTitle()],
                    color: ttcSelection.segment.color,
                    'data-group-name': getDataGroupName({plural: false}),
                  },
                  {
                    cohort: ttcSelection.cohort,
                    cohortDetails: ttcSelection.cohortDetails,
                  },
                );
              } else {
                throw new Error(`unsupported action`);
              }
            },
            /** place the popover 5px below the clicked bar */
            getPopoverPlacement: () => {
              if (!this.state.ttcSelection) {
                return null;
              }

              const {clientX, clientY} = this.state.ttcSelection;
              return {
                // align to the click position horizontally
                left: `${clientX}px`,
                // align to beneath the bar vertically
                top: `${clientY + 10}px`,
              };
            },
          },
        },
        frequency: {
          getAttrs: () => ({
            'include-header': true,
            'hide-tooltip': !!this.state.frequencySelection,
          }),
          getAggregateAttrs: () => ({
            'chart-view-type': this.state.funnelQuery.frequencyType,
            'project-id': getProjectId(),
            'hide-tooltip': !!this.state.frequencySelection,
            'project-as-of-date': this.getProjectAsOfDate(),
            'project-timezone': this.getProjectTimezone(),
          }),
          getCohortNameAndDescription: ({eventIndex, type, segment, conversionRange}) => {
            return {
              description: this.getCohortDescription({
                behaviorDescriptor: {
                  index: eventIndex,
                  type,
                },
                segment,
                dataGroup: this.getActiveDataGroup(),
                conversionRange,
                selectedSteps: this.stepSelected(),
              }),
              name: cohorts.getCohortTitle({
                index: eventIndex,
                type,
                reportName: this.savedReportName,
                dataGroup: this.getActiveDataGroup(),
                conversionRange,
                selectedSteps: this.stepSelected(),
                ...(this.isQuerySegmented() && {segment}),
              }),
            };
          },
          isConversionRate: () => {
            return this.state.funnelQuery.frequencyType === FrequencyViewType.ConversionRate;
          },

          /** Popover Methods */
          popover: {
            buttonOptions: () => {
              const options = [];
              const dataGroupName = getDataGroupName();
              const disableMessage = this._bookmarkController.getDisableViewUsersButtonMessage();
              if (
                [FrequencyViewType.ConversionRate, FrequencyViewType.Conversion].includes(
                  this.state.funnelQuery.frequencyType,
                )
              ) {
                options.push({
                  label: `View converted ${dataGroupName}`,
                  icon: `team`,
                  value: ViewUsersType.Converted,
                  ...(disableMessage
                    ? {
                        isDisabled: true,
                        tooltip: disableMessage,
                      }
                    : {}),
                });
              }
              if (
                [FrequencyViewType.ConversionRate, FrequencyViewType.Dropoff].includes(
                  this.state.funnelQuery.frequencyType,
                )
              ) {
                options.push({
                  label: `View dropped off ${dataGroupName}`,
                  icon: `team`,
                  value: ViewUsersType.DroppedOff,
                  ...(disableMessage
                    ? {
                        isDisabled: true,
                        tooltip: disableMessage,
                      }
                    : {}),
                });
              }
              return options;
            },
            closePopover: () => {
              if (this.state.frequencySelection?.point) {
                deselectPoint(this.state.frequencySelection.point);
              }
              this.update({frequencySelection: null});
            },
            cohortDetails: (
              frequencySelection: BarChartPopover,
              comparisonDateRange: Nullable<CalendarDateRange>,
            ): CohortDetail[] => {
              const behaviorDescriptor = {
                index: this.state.funnelQuery.eventIndex,
                bucket: frequencySelection.queryIndex,
                fromStart: false,
                groupByValue: frequencySelection.segment?.name,
              } as Omit<FrequencyBucketDescriptor, 'type'>;
              const query = new FunnelsQuery(this.state.funnelQuery);
              if (comparisonDateRange) {
                query.dateRange = new DateRange({
                  type: DateRangeType.Between,
                  from: comparisonDateRange.from,
                  to: comparisonDateRange.to,
                });
              }
              return [
                [FrequencyViewType.ConversionRate, FrequencyViewType.Conversion].includes(
                  this.state.funnelQuery.frequencyType,
                ) && {
                  warning: ``,
                  label: `Create cohort from conversion`,
                  type: CohortType.FREQUENCY_CONVERSION,
                  cohort: cohorts.createCohort({
                    ...this.helpers.frequency.getCohortNameAndDescription({
                      type: CohortType.FREQUENCY_CONVERSION,
                      conversionRange: frequencySelection.rowLabel,
                      segment: convertSegmentLabelForCohort(frequencySelection.segment.label, this.getSegmentNames()),
                      eventIndex: this.state.funnelQuery.eventIndex,
                    }),
                    behaviorDescriptor: {
                      ...behaviorDescriptor,
                      type: CohortType.FREQUENCY_CONVERSION,
                    },
                    bookmark: query.generateBookmark(),
                    query,
                  }),
                },
                [FrequencyViewType.ConversionRate, FrequencyViewType.Dropoff].includes(
                  this.state.funnelQuery.frequencyType,
                ) && {
                  type: CohortType.FREQUENCY_DROP_OFF,
                  label: `Create cohort from drop off`,
                  warning: ``,
                  cohort: cohorts.createCohort({
                    ...this.helpers.frequency.getCohortNameAndDescription({
                      type: CohortType.FREQUENCY_DROP_OFF,
                      conversionRange: frequencySelection.rowLabel,
                      segment: convertSegmentLabelForCohort(frequencySelection.segment.label, this.getSegmentNames()),
                      eventIndex: this.state.funnelQuery.eventIndex,
                    }),
                    behaviorDescriptor: {
                      ...behaviorDescriptor,
                      type: CohortType.FREQUENCY_DROP_OFF,
                    },
                    bookmark: query.generateBookmark(),
                    query,
                  }),
                },
              ].filter(Boolean);
            },
            getPopoverPlacement: () => {
              if (!this.state.frequencySelection) {
                return null;
              }

              const {clientX, clientY} = this.state.frequencySelection;
              return {
                // align to the click position horizontally
                left: `${clientX}px`,
                // align to beneath the bar vertically
                top: `${clientY + 10}px`,
              };
            },
            handlePopoverAction: (ev) => {
              const {action} = ev.detail;
              const {frequencySelection, funnelQuery} = this.state;
              if ([ViewUsersType.Converted, ViewUsersType.DroppedOff].includes(action)) {
                this.track(PopoverTracking.VIEW_USERS, {
                  chart: ChartType.Frequency,
                });
                const cohortDetails = frequencySelection.cohortDetails.filter((c) => {
                  const isConverted = action === ViewUsersType.Converted;
                  const type = isConverted ? CohortType.FREQUENCY_CONVERSION : CohortType.FREQUENCY_DROP_OFF;
                  return c.type === type;
                });
                const {groupBy: groupByList} = funnelQuery;
                openUserSidebar(
                  {
                    'hide-cohort-functionality': groupByList.some(isNumericSegment),
                    'include-all-users': true,
                    'report-name': ReportName.Funnels,
                    segments: [`${frequencySelection.rowLabel} times`, this.helpers.frequency.popover.popoverTitle()],
                    'segment-label': action === ViewUsersType.Converted ? `who converted` : `who dropped off`,
                    color: frequencySelection.segment.color,
                    'data-group-name': getDataGroupName({plural: false}),
                  },
                  {
                    cohort: cohortDetails[0].cohort,
                    cohortDetails,
                  },
                );
              } else {
                throw new Error(`unsupported action`);
              }
            },
            openPopover: (ev: CustomEvent) => {
              this.track(PopoverTracking.CLICK_OPEN, {
                chart: ChartType.Frequency,
              });
              const frequencySelection = ev.detail.selection as BarChartPopover;
              if (frequencySelection.point) {
                selectPoint(frequencySelection.point);
              }
              const comparisonDateRange = ev.detail.comparisonDateRange as Nullable<CalendarDateRange>;

              const cohortDetails = this.helpers.frequency.popover.cohortDetails(
                frequencySelection,
                comparisonDateRange,
              );
              this.update({
                frequencySelection: {
                  ...frequencySelection,
                  cohortDetails,
                  cohort: cohortDetails[0].cohort,
                },
              });
            },
            popoverBody: () => {
              const {eventIndex, frequencyType, countType, dataGroupId} = this.state.funnelQuery;
              const {frequencySelection} = this.state;
              if (frequencySelection) {
                const {
                  segment: {count, name},
                } = frequencySelection;
                const segment: FunnelSegment = this.getRenamedOrderedSegmentSteps().find(
                  (segment) => segment.prop === name,
                );
                const stepIndex = eventIndex || 0;
                const funnelStep = getFunnelStep(segment.steps, stepIndex);
                const bucketData = getBucketData(funnelStep, conversionMode(frequencyType).bucketAccessor);
                const countTypeText = getCountTypeText({
                  funnelsCountType: countType,
                  dataGroupId,
                });
                const funnelConversionText = conversionMode(frequencyType).representationText;
                return [
                  {
                    count: `${commaizeNumber(count)} ${countTypeText}`,
                    percent: `${percentOfTotal(bucketData, count)}% ${funnelConversionText}`,
                    subText: `${frequencySelection.rowLabel} times`,
                  },
                ];
              }
              return null;
            },
            popoverBodyForConversionRate: () => {
              const {eventIndex, frequencyType} = this.state.funnelQuery;
              const {frequencySelection} = this.state;
              if (frequencySelection) {
                const {
                  segment: {count, name},
                } = frequencySelection;
                const segment: FunnelSegment = this.getRenamedOrderedSegmentSteps().find(
                  (segment) => segment.prop === name,
                );
                const stepIndex = eventIndex || 0;
                const funnelStep = getFunnelStep(segment.steps, stepIndex);
                const conversionData = getBucketData(funnelStep, FunnelStepBucketAccessors.ConvertStepFreqBuckets);
                const dropOffData = getBucketData(funnelStep, FunnelStepBucketAccessors.DropoffStepFreqBuckets);
                const funnelConversionText = conversionMode(frequencyType).representationText;
                return [
                  {
                    usersDroppedOff: `${dropOffData.buckets[frequencySelection.queryIndex]}  users dropped off`,
                    usersConverted: `${conversionData.buckets[frequencySelection.queryIndex]}  users converted`,
                    percent: `${round(count * 100, 2)}% ${funnelConversionText}`,
                    subText: `${frequencySelection.rowLabel} times`,
                  },
                ];
              }
              return null;
            },
            popoverTitle: () => {
              const {frequencySelection} = this.state;
              return frequencySelection ? frequencySelection.segment.label : null;
            },
          },
        },
      },
    };
  }

  drilldown(index) {
    const {funnelQuery: query, segmentData} = this.state;

    // TODO(sam): We really only need the bucket data for drilling down. This is copy/pasted for ease, but ideally
    // we should pull that data from the popover or just calculate it from state so that we can remove the data calls
    const overallStep = !this.isQueryGroupedSteps()
      ? getFunnelStep(getOverallStepsFromDatedSegments(segmentData), this.state.funnelQuery.eventIndex)
      : null;
    const segments = [];

    const funnelData = createBucketLineChartFunnelData({
      segmentData: this.getRenamedOrderedSegmentSteps(),
      selected: this.getSelectedSegments(),
      stepIndex: this.state.funnelQuery.eventIndex,
      hasComparison: this.hasComparison,
      isComparisonData: false,
    });
    const comparisonData = this.hasComparison
      ? createBucketLineChartFunnelData({
          segmentData: getSegments(this.state.comparisonSegmentData),
          selected: this.getSelectedSegments(),
          stepIndex: this.state.funnelQuery.eventIndex,
          hasComparison: this.hasComparison,
          isComparisonData: true,
        })
      : null;

    funnelData.forEach((value, key) => {
      if (comparisonData) {
        segments.push(comparisonData.get(key));
      }
      segments.push(value);
    });

    const data = getBarChartData({
      segmentData: segments,
      overallStep,
      selectedBucket: this.state.funnelQuery.timeBuckets,
      // Typecast because selectedBar isn't that important to getting the chart data in this instance (because we probably don't need this func call?)
      selection: this.state.ttcSelection as BarChartSelectedBar,
      bucketAccessor: getBucketAccessor(this.state.funnelQuery.eventIndex),
      medianAccessor: getMedianAccessor(this.state.funnelQuery.eventIndex),
      labelFormatter,
      showAnnotation: !query.isAnyOrderFunnel(),
    });

    const {timeBuckets} = query;
    const timeSelection = formatTime(timeBuckets.min, timeBuckets.max);

    const bucket = data[index];

    this.track(TTC_DRILLDOWN, {
      selection: bucket.label,
      previous_selection: timeSelection,
      selection_index: index,
      event_index: this.state.funnelQuery.eventIndex || `overall`,
    });

    this.updateQuery({
      timeBuckets: {
        min: bucket.start,
        max: bucket.end,
        bucketSize: getBucketSize(bucket.end - bucket.start),
        offset: bucket.start,
        isDrilledDown: true,
      },
    });
  }
  handleRCAClicked(selectedDateRange) {
    const url = `${getReportUrl(`boards`, {
      projectId: this.projectId,
      workspaceId: this.workspaceId,
    })}#rca=${this.attr(`rca-source-id`)}&date=${selectedDateRange.to}&type=bookmark`;
    window.open(url);
  }

  getCohortDescription({
    behaviorDescriptor,
    segment,
    dataGroup,
    conversionRange,
    selectedSteps,
  }: {
    behaviorDescriptor: {type: string; index: number};
    segment: string;
    dataGroup?: DataGroup;
    conversionRange: string;
    selectedSteps?: StepSelection;
  }): string {
    const steps = getOverallStepsFromDatedSegments(this.state.segmentData);
    if (!steps.length) {
      return ``;
    }

    const {index, type} = behaviorDescriptor;
    let stepNames = this.state.funnelQuery.getStepLabels();
    if (selectedSteps) {
      stepNames = selectedSteps.isEntireRange
        ? stepNames
        : stepNames.slice(selectedSteps.range.from - 1, selectedSteps.range.to);
    } else {
      stepNames = stepNames.slice(0, index + 1);
    }

    const reportName = this.savedReportName;
    const {exclusionSteps, dateRange} = this.state.funnelQuery;
    return cohorts.getCohortText({
      cohortType: type,
      stepNames,
      exclusionSteps,
      reportName,
      dateRange,
      dataGroup,
      conversionRange,
      ...(this.isQuerySegmented() && {segment}),
    });
  }

  closePopover() {
    this.update({popoverState: {open: false}, selectedFunnelStepSegment: null});
  }

  handleCancelCohort = (chart: ChartType) => (
    ev: CustomEvent<{name: string; description: string; type: CohortType}>,
  ) => {
    const {type, name, description} = ev.detail;
    if (chart === ChartType.Chart) {
      const {
        bar: {index: stepIndex, type: barType},
      } = this.state.popoverState;
      this.trackChartPopover({
        eventName: PopoverTracking.CANCEL_COHORT,
        stepIndex,
        barType,
        properties: {
          'cohort type': type,
          'cohort name': name,
          'cohort description': description,
        },
      });
    } else {
      this.track(PopoverTracking.CANCEL_COHORT, {
        'cohort type': type,
        'cohort name': name,
        'cohort description': description,
        chart,
      });
    }
  };

  handleCohortModalOpen = (chart: ChartType) => (
    ev: CustomEvent<{name: string; description: string; type: CohortType}>,
  ) => {
    const {type} = ev.detail;
    if (chart === ChartType.Chart) {
      const {
        bar: {index: stepIndex, type: barType},
      } = this.state.popoverState;

      this.trackChartPopover({
        eventName: PopoverTracking.CLICK_COHORT,
        stepIndex,
        barType,
        properties: {
          'cohort type': type,
        },
      });
    } else {
      this.track(PopoverTracking.CLICK_COHORT, {
        'cohort type': type,
        chart,
      });
    }
  };

  handleFailSaveCohort = (chart: ChartType) => (
    ev: CustomEvent<{name: string; description: string; type: CohortType}>,
  ) => {
    const {type, name, description} = ev.detail;
    this.track(PopoverTracking.SAVE_FAILED, {
      'cohort type': type,
      'cohort name': name,
      'cohort description': description,
      chart,
    });
  };

  handleSaveCohort = (chart: ChartType) => (ev: CustomEvent<{name: string; description: string; type: CohortType}>) => {
    const {name, description, type} = ev.detail;
    if (chart === ChartType.Chart) {
      const {
        bar: {index: stepIndex, type: barType},
      } = this.state.popoverState;
      this.trackChartPopover({
        eventName: PopoverTracking.SAVE_COHORT,
        stepIndex,
        barType,
        properties: {
          'cohort type': type,
          'cohort name': name,
          'cohort description': description,
          'visibility feature flag enabled': true,
        },
      });
    } else {
      this.track(PopoverTracking.SAVE_COHORT, {
        'cohort type': type,
        'cohort name': name,
        chart,
        'cohort description': description,
        'visibility feature flag enabled': true,
      });
    }
  };

  /**
   * Get all data segments, transformed by the top-paths rename and ordered by conversions
   */
  getRenamedOrderedSegmentSteps(): FunnelSegment[] {
    const {funnelQuery, segmentData} = this.state;
    return getRenamedOrderedSegmentSteps(funnelQuery, segmentData);
  }

  getChartVizType(): ChartVizType {
    switch (this.state.funnelQuery.chartType) {
      case ChartType.Chart:
        return this.state.funnelQuery.stepsVizType ?? ChartVizType.Steps;
      case ChartType.Frequency:
        return this.state.funnelQuery.frequencyVizType;
      case ChartType.TimeToConvert:
        return this.state.funnelQuery.ttcVizType;
      case ChartType.Trends:
        return ChartVizType.Line;
      case ChartType.Sankey:
        return ChartVizType.Sankey;
      default:
        return null;
    }
  }

  stepSelected() {
    return stepSelectorAttributes(this.state.funnelQuery).selected;
  }

  getSelectedSegments(): ColorChoice[] {
    return getSelectedSegments(
      this.state.funnelQuery.selected,
      this.bookmarkParams.selectMode,
      this.getRenamedOrderedSegmentSteps(),
      this.state.funnelQuery.getCommonChartType(),
    );
  }

  /** Get raw property names for all breakdowns applied */
  getSegmentNames() {
    return this.state.funnelQuery.getSegmentNames();
  }

  trackOda(eventName, properties = {}) {
    this.track(eventName, {
      // these are generic because the ML pipeline is generic in this way
      bookmark_type: `funnels`,
      ...properties,
    });
  }

  trackChartPopover({
    eventName,
    stepIndex,
    barType,
    properties = {},
  }: {
    eventName: PopoverTracking;
    stepIndex: number;
    barType: BarType;
    properties?: {[key: string]: any};
  }) {
    const steps = getOverallStepsFromDatedSegments(this.state.segmentData);
    this.track(eventName, {
      'event name': this.state.funnelQuery.getStepLabels()[stepIndex],
      tab: `funnels`,
      chart: `funnels-chart`,
      'clicked element': `step ${stepIndex + 1}`,
      'element type': barType,
      'is last step': stepIndex + 1 === steps.length,
      ...properties,
    });
  }

  track(eventName, properties = {}) {
    const event: TrackEvent = new CustomEvent(`track`, {detail: {eventName, props: properties}});
    this.dispatchEvent(event);
  }

  updateQuery(delta: Partial<FunnelsQuery>) {
    const event: UpdateEvent = new CustomEvent(`update`, {detail: {delta}});
    this.dispatchEvent(event);
  }
}

export const FunnelsChartDisplayEl = defineAndMakeJsxFactory<FunnelsChartDisplayJsxProps>(
  `funnels-chart-display`,
  FunnelsChartDisplay,
);
export const FunnelsChartDisplayReact = panel2react<FunnelsChartDisplayJsxProps>(
  `funnels-chart-display`,
  FunnelsChartDisplay,
);
